import {
  PayrollLegalEntitiesDocument,
  TeamSystemCompensationsExportFeatureDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { buildPayrollConceptsByLegalEntitySettings } from 'modules/payroll_bundle/lib/settingsPayrollConcept'
import EmployeesData from 'modules/payroll_integrations/components/Content/EmployeeData'
import Table from 'modules/payroll_integrations/components/Table'
import PayrollIntegration from 'modules/payroll_integrations/lib/catalog'
import type { Codeable, ColumnNames } from 'modules/payroll_integrations/lib/types'

const INTEGRATION_NAME = PayrollIntegration.teamSystem

const TeamSystemIntegrationContent = () => {
  const { company } = useCurrentSession()

  const columnNames: ColumnNames = [
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_team_system.settings.form.header.concept'
      ),
      colSpan: 4,
      attribute: 'concept',
    },
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_team_system.settings.form.header.value'
      ),
      colSpan: 4,
      attribute: 'value',
    },
  ]

  const { hasCompensations, loading: loadingHasCompensations, error: errorHasCompensations } =
    useHasCompensations()

  const { data: featureFlagData, loading: loadingFeatureFlag, error: errorFeatureFlag } = useQuery(
    TeamSystemCompensationsExportFeatureDocument,
    {
      variables: { companyId: company.id },
    }
  )

  const hasTeamSystemCompensationsExport =
    featureFlagData?.features?.featuresConnection?.nodes?.[0]?.enabled ?? false

  const { data: legalEntitiesData, loading: loadingLegalEntities, error: errorLegalEntities } =
    useQuery(PayrollLegalEntitiesDocument, {
      variables: { companyIds: [company.id] },
    })
  const legalEntities = (legalEntitiesData?.companies?.legalEntitiesConnection?.nodes || []).map(
    (node) => ({ ...node, legalName: node.name })
  )

  const { concepts: payrollConcepts, loading: loadingConcepts } = useConcepts(
    undefined,
    [
      'deductions',
      'earnings_benefits_in_kind',
      'earnings_fixed_salary',
      'earnings_others',
      'earnings_variable',
    ],
    true,
    !hasCompensations
  )

  const { data: companyInstallationsData, loading: loadingCompanyInstallations } =
    useGetMarketplaceCompanyInstallations(
      {
        integrationDatoIds: [integrationCodes.team_system],
      },
      !hasCompensations
    )

  const teamSystemInstallation = companyInstallationsData[0]

  const payrollConceptsByLegalEntity = buildPayrollConceptsByLegalEntitySettings(
    payrollConcepts,
    legalEntities
  )

  const codeables: Record<string, Codeable> = {
    legalEntity: {
      position: 0,
      collection: legalEntities,
      class: 'LegalEntity',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_team_system.settings.form.concept.legal_entity'
      ),
    },
  }

  if (hasCompensations && hasTeamSystemCompensationsExport) {
    codeables.payrollConcepts = {
      position: 1,
      collection: payrollConceptsByLegalEntity || [],
      class: 'Payroll::Concept',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_team_system.settings.form.payroll_concept'
      ),
    }
  }

  const loading =
    loadingHasCompensations ||
    loadingFeatureFlag ||
    loadingLegalEntities ||
    loadingConcepts ||
    loadingCompanyInstallations

  const error = errorHasCompensations || errorFeatureFlag || errorLegalEntities

  return (
    <ResponsivePage.Body variant="full" loading={loading} error={error ?? null}>
      <Table
        columnNames={columnNames}
        codeables={codeables}
        integration={INTEGRATION_NAME}
        integrationInstallation={teamSystemInstallation}
      />
      <EmployeesData
        integration_name={i18n.t(
          'app_store_v2.app_integrations.payroll_integrations_team_system.integration_name'
        )}
      />
    </ResponsivePage.Body>
  )
}

export default TeamSystemIntegrationContent
